(ns app.front.ui.service-hub-panel
  "Global Service Hub side panel: quick navigation to workspace search pages and
   other useful pages. Links are shown based on the user's permissions and group
   configuration. Gated behind the :feature-flag.id/service-hub-panel flag."
  (:require ["@carbon/icons-react/es/dashboard/24" :default Dashboard24]
            [app.front.dashboard.code-enforcement :as dashboard-code-enforcement]
            [app.front.dashboard.contractor-registration :as dashboard-contractor-registration]
            [app.front.dashboard.licencing :as dashboard-licencing]
            [app.front.ui.panels :as panels]
            [app.front.ui.side-panel :as side-panel]
            [app.front.util :refer [for-frag]]
            [app.front.view.search.subs :as search-subs]
            [core.front.event :refer [<sub]]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]))

(rf/reg-sub ::panel-open?
  :<- [::panels/open? :service-hub]
  :-> boolean)

(defn- fetch-link-counts-fx [workspace-search-updates?]
  (if workspace-search-updates?
    [[:dispatch [::search-subs/fetch-overall-counts]]]
    [[:dispatch [::dashboard-code-enforcement/queue-fetch-open-complaint-total]]
     [:dispatch [::dashboard-licencing/queue-fetch-licence-count]]
     [:dispatch [::dashboard-contractor-registration/queue-fetch-registration-count]]]))

(rf/reg-event-fx ::toggle-open
  (fn [{:keys [db]} _]
    (let [opening? (not= :service-hub (-> db :ui/open-panel first))]
      {:fx (cond-> [[:dispatch [::panels/toggle :service-hub]]]
             opening? (into (fetch-link-counts-fx (ff/enabled? :feature-flag.id/workspace-search-updates))))})))

(rf/reg-sub ::complaints-visible?
  :<- [:session/code-enforcement-authority?]
  :<- [:session/work-orders-authority?]
  (fn [[code-enforcement? work-orders?]] (boolean (or code-enforcement? work-orders?))))


(rf/reg-sub ::search-tab-visible?
  (fn [db [_ tab]]
    (and (:session/user db)
         (search-subs/tab-available? db tab))))

(defn- management-view-groups [user]
  (let [authority-groups   (->> user :user/authority-groups (remove :via-superdomain?))
        domain-groups      (->> user :user/domain-groups (remove :via-superdomain?))
        superdomain-groups (->> user
                                :user/superdomain-groups
                                (remove (comp empty? :management-views))
                                (sort-by :group/name))
        other-groups       (:user/administered-groups user)]
    (->> (concat superdomain-groups authority-groups domain-groups other-groups)
         (remove (comp empty? :management-views))
         (util/distinct-by :db/id))))

(rf/reg-sub ::management-view-link
  :<- [:session/user]
  (fn [user [_ route-name]]
    (when-let [group-id (some->> (management-view-groups user)
                                 (filter #(some #{route-name} (:management-views %)))
                                 first
                                 :db/id)]
      {:route-name route-name
       :params     {:group-id group-id}})))

(defn search-items [tr]
  (mapv (fn [{:keys [id label-key]}]
          {:route-name  :view/search
           :query       {:selected-tab (name id)}
           :label       (tr [label-key])
           :icon        Dashboard24
           :visible-sub [::search-tab-visible? id]
           :count-sub   [::search-subs/overall-count id]})
        search-subs/entity-tabs))

(defn- items-data
  "Data describing the panel items. Link entries have a `:visible-sub` or `:link-sub` deciding
   whether the current user may see the link. Empty item sections are removed after hidden links are filtered out."
  [tr workspace-search-updates?]
  [[{:route-name  :dashboard
     :label       (tr [:service-hub/dashboard])
     :icon        Dashboard24
     :visible-sub [:session/logged-in?]}]
   (if workspace-search-updates?
     (search-items tr)
     [{:route-name  :view/licence-search
       :label       (tr [:licencing/licencing])
       :icon        Dashboard24
       :count-sub   [::dashboard-licencing/licence-count]
       :visible-sub [::dashboard-licencing/show-dashlet?]}
      {:route-name  :view/contractor-registration-search
       :label       (tr [:service-hub/contractors])
       :icon        Dashboard24
       :count-sub   [::dashboard-contractor-registration/registration-count]
       :visible-sub [::dashboard-contractor-registration/show-dashlet?]}
      {:route-name  :view/code-enforcement-complaints
       :label       (tr [:code-enforcement/complaints])
       :icon        Dashboard24
       :count-sub   [::dashboard-code-enforcement/open-complaint-total]
       :visible-sub [::complaints-visible?]}
      {:route-name  :view/property-dashboard
       :label       (tr [:group.feature/property-management])
       :icon        Dashboard24
       :visible-sub [:session/property-management-visible?]}])
   [{:route-name :view/group.admin.bonds
     :label      (tr [:bond/bonds])
     :icon       Dashboard24
     :link-sub   [::management-view-link :view/group.admin.bonds]}
    {:route-name :view/group.admin.escrow
     :label      (tr [:payment-method/escrow])
     :icon       Dashboard24
     :link-sub   [::management-view-link :view/group.admin.escrow]}]])

(defn- item-target [{:keys [route-name params query visible-sub link-sub]}]
  (or (some-> link-sub <sub)
      (when (and visible-sub (<sub visible-sub))
        (util/mmap route-name params query))))

(defn- visible-item-sections [tr]
  (->> (items-data tr (<sub [::ff/enabled? :feature-flag.id/workspace-search-updates]))
       (keep (fn [item-section]
               (->> item-section
                    (keep (fn [{:keys [count-sub] :as item}]
                            (some-> (item-target item)
                                    (merge item)
                                    (assoc :count (some-> count-sub <sub)))))
                    vec
                    not-empty)))
       doall))

(defn- link-href [{:keys [route-name params query]}]
  (cond
    query  (href/href route-name params query)
    params (href/href route-name params)
    :else  (href/href route-name)))

(defn- item-id [{:keys [route-name query]}]
  (or (:selected-tab query) (name route-name)))

(defn service-hub-link [{:keys [label icon active? count href] :as item}]
  [:a
   {:href         (or href (link-href item))
    :data-cy      (str "service-hub-link--" (item-id item))
    :aria-current (when active? "page")
    :class        ["flex items-center gap-3 w-full !p-2 rounded-md !no-underline"
                   "!text-ui-night-sky-100 border border-solid"
                   (if active?
                     "bg-ui-violet-10 border-ui-violet-100 font-bold"
                     "border-transparent hover:bg-ui-blue-10 hover:border-ui-violet-100 focus:bg-ui-blue-10 focus:border-ui-violet-100")]}
   (when icon [:> icon])
   [:span.flex-1 label]
   (when (some? count)
     [:span.font-semibold {:data-cy (str "service-hub-count--" (item-id item))} count])])

(defn active-item? [{:keys [route-name query]} current-route selected-tab]
  (and (= route-name current-route)
       (or (nil? (:selected-tab query))
           (= (:selected-tab query) (name selected-tab)))))

(defn- service-hub-item-section [item-section current-route selected-tab]
  [:div {:class "flex flex-col gap-2 py-2 first:pt-0 last:pb-0 border-t border-solid border-gray-300 first:border-t-0"}
   (for-frag [item item-section]
     [service-hub-link (assoc item :active? (active-item? item current-route selected-tab))])])

(defn- service-hub-item-sections [item-sections current-route selected-tab]
  [:div {:class "flex flex-col"}
   (for-frag [item-section item-sections]
     [service-hub-item-section item-section current-route selected-tab])])

(defn service-hub-panel [tr]
  (let [panel-open?   (<sub [::panel-open?])
        current-route (<sub [:route/name])
        item-sections (visible-item-sections tr)
        selected-tab  (<sub [::search-subs/selected-entity-tab])]
    [side-panel/side-panel {:panel-open? panel-open? :panel-id :service-hub}
     [service-hub-item-sections item-sections current-route selected-tab]]))
